import { checkAzureArtifact } from '../bootstrap/azure/compatibility.js';
import { DELEGATION_VERSION, delegationCompatibility, } from '../bootstrap/azure/delegation-template.js';
import { AZURE_DEPLOY_ACCESS_VERSION } from '../bootstrap/azure/deploy-access.js';
import { AZURE_RUNNER_VERSION } from '../bootstrap/azure/runner-template.js';
import { RUN_CEILING_HOURS } from '../runs/execution.js';
import { GRANT_WARNING_DAYS, describeDaysLeft, grantDaysLeft } from './grant.js';
import { decide } from './onboarding.js';
/**
 * Onboarding an Azure subscription (IM-45): the step list, as data the shared
 * `decide` rule reads.
 *
 * Fewer steps than AWS, and every one is asked of the subscription, because
 * nothing here is regional (see `buildRunnerTemplate`). No `completions` step:
 * completion is polled through Reader, so there is nothing in the customer's
 * subscription to wire. No `redaction` step yet: the rules ship inside the
 * runner template since runner v2 (IM-46), so for now the runner's own version
 * says which rules a subscription runs, and a step of its own is a follow-up.
 *
 * The instance template is not here either: it is per instance, so
 * `assessAzureInstance` asks about it for one instance at a time.
 *
 * The delegation comes first, as the access stack does on AWS: until it exists
 * Qrvey can read nothing in the subscription, and every other step is unknown.
 *
 * The ids are their own, not AWS's under the same names. The console keys on
 * AWS's (`web/src/views/account.tsx`), and a step called `grant` that means a
 * client secret rather than a stack would be the same word for two things.
 */
export const AZURE_ONBOARDING_STEPS = [
    'delegation',
    'deploy-app',
    'runner',
    'runner-registry',
    'deploy-grant',
];
/**
 * What this build expects, from its own constants. Azure has no recommended
 * image or per-tenant default to look up, so unlike AWS's `expectationsFor`
 * there is nothing to read: the versions are the whole of it.
 */
export const AZURE_ONBOARDING_EXPECTATIONS = {
    delegationVersion: DELEGATION_VERSION,
    runnerVersion: AZURE_RUNNER_VERSION,
    deployAccessVersion: AZURE_DEPLOY_ACCESS_VERSION,
};
/**
 * Whether the deploy secret outlasts a run started now (IM-46): the Azure
 * `grantOutlastsRun`. No version enters into it, unlike AWS's: azurerm and
 * `az` renew their tokens from the secret on every runner, so every Azure run
 * is AWS v15's case and is cut off at its first token after the expiry. The
 * deploy-grant step, the start path and the dispatcher all hold this.
 * Undefined when there is no readable expiry, which never refuses.
 */
export const azureGrantOutlastsRun = (grantExpiresAt, now) => {
    if (grantExpiresAt === undefined)
        return undefined;
    const expires = new Date(grantExpiresAt).getTime();
    if (Number.isNaN(expires))
        return undefined;
    return expires > now.getTime() + RUN_CEILING_HOURS * 3_600_000;
};
const step = (id, title, state, detail, nextAction) => ({
    id,
    title,
    scope: 'account',
    state,
    detail,
    ...(nextAction === undefined ? {} : { nextAction }),
});
const DEPLOY_DELEGATION = 'Download the delegation template from this screen and ask the customer to deploy it at subscription scope. Under resource-group delegation it is deployed once for the runner’s group and once for each instance’s.';
/*
 * Blocking whenever the delegation is not this account's own, read-only one.
 * Absent, nothing a run does can be observed or checked, since completion is
 * polled through it. Foreign or widened, IM-44's rule is that Qrvey does not
 * act on it at all, so a run would be acting on exactly the access the health
 * check refuses.
 */
const delegationStep = (facts, expect) => {
    const title = 'Delegation';
    if (facts.delegationUnreadable === true || facts.delegation === undefined) {
        return {
            step: step('delegation', title, 'unknown', facts.unreadableReason ?? ''),
            blocks: true,
        };
    }
    switch (facts.delegation) {
        case 'absent':
            return {
                step: step('delegation', title, 'attention', 'Not delegated to Qrvey, or the delegation has been removed', DEPLOY_DELEGATION),
                blocks: true,
            };
        case 'foreign-mark':
            return {
                step: step('delegation', title, 'attention', 'Delegated to Qrvey, but not by this account’s template', `${DEPLOY_DELEGATION} If the subscription was registered twice, hide the registration that is not in use.`),
                blocks: true,
            };
        case 'widened':
            return {
                step: step('delegation', title, 'attention', 'Grants Qrvey more than read-only access', `${DEPLOY_DELEGATION} It grants built-in Reader alone.`),
                blocks: true,
            };
        case 'delegated': {
            const version = facts.delegationVersion;
            const compatibility = delegationCompatibility(version);
            const deployed = version === undefined ? 'Delegated' : `v${String(version)} delegated`;
            if (compatibility.state === 'ahead' || version === expect.delegationVersion) {
                return { step: step('delegation', title, 'done', deployed), blocks: false };
            }
            // Access works at any version this build reads; an older one is a
            // redeploy to schedule, not a refusal.
            return {
                step: step('delegation', title, 'attention', `${deployed}, v${String(expect.delegationVersion)} expected`, DEPLOY_DELEGATION),
                blocks: false,
            };
        }
    }
};
const RUN_CREATE = 'Download the deploy-access script from this screen and ask the customer to run its create command; it prints the client id the runner template asks for.';
const deployAppStep = (facts) => {
    if (facts.runnerUnreadable === true) {
        return { step: step('deploy-app', 'Deploy app', 'unknown', ''), blocks: true };
    }
    if (facts.deployAppClientId !== undefined) {
        return {
            step: step('deploy-app', 'Deploy app', 'done', `The runner signs in as ${facts.deployAppClientId}`),
            blocks: false,
        };
    }
    return {
        step: step('deploy-app', 'Deploy app', 'attention', 'No runner names a deploy app', RUN_CREATE),
        blocks: true,
    };
};
const runnerStep = (facts, expect) => {
    if (facts.runnerUnreadable === true) {
        return { step: step('runner', 'Runner', 'unknown', ''), blocks: true };
    }
    const deploy = 'Download the runner template from this screen and ask the customer to deploy it into a resource group of its own, never an instance’s.';
    if (facts.runnerConflict !== undefined && facts.runnerConflict.length > 1) {
        return {
            step: step('runner', 'Runner', 'unknown', `A runner in each of ${facts.runnerConflict.join(', ')}; one is expected`, 'Ask the customer which resource group holds the runner they use, and to delete the other deployment. Qrvey does not guess which one a run would go to.'),
            blocks: true,
        };
    }
    if (facts.runnerVersion === undefined) {
        return {
            step: step('runner', 'Runner', 'attention', facts.delegationScope === 'resource-groups'
                ? 'No runner in any resource group delegated to Qrvey'
                : 'Not deployed', facts.delegationScope === 'resource-groups'
                ? `${deploy} If it is deployed already, its group is not delegated: deploy the delegation template for that group too.`
                : deploy),
            blocks: true,
        };
    }
    const compatibility = checkAzureArtifact('runner', facts.runnerVersion);
    const deployed = `v${facts.runnerVersion} deployed`;
    if (facts.dispatcherMissing === true) {
        return {
            step: step('runner', 'Runner', 'attention', `${deployed}, with no dispatcher beside it`, 'The runner template did not finish deploying: its dispatcher Logic App is missing, so no run can be started. Ask the customer to redeploy the runner template into the same resource group.'),
            blocks: true,
        };
    }
    if (facts.dispatcherVersion !== undefined && facts.dispatcherVersion !== facts.runnerVersion) {
        // One template made both, so two versions is a partial redeploy. A run
        // still starts; the dispatcher is what checks the request, so it is said.
        return {
            step: step('runner', 'Runner', 'attention', `${deployed}, its dispatcher at v${facts.dispatcherVersion}`, 'Ask the customer to redeploy the runner template into the same resource group, so the job and the dispatcher come from one version.'),
            blocks: compatibility.state === 'too-old' || compatibility.state === 'unreadable',
        };
    }
    switch (compatibility.state) {
        case 'current':
            // Against the expectation as well as the published constant, so a test
            // can move the expectation and see the step say "behind".
            if (facts.runnerVersion === expect.runnerVersion) {
                return { step: step('runner', 'Runner', 'done', deployed), blocks: false };
            }
            return {
                step: step('runner', 'Runner', 'attention', `${deployed}, v${expect.runnerVersion} expected`, `Download the runner template from this screen and ask the customer to redeploy it. v${expect.runnerVersion} is needed.`),
                blocks: false,
            };
        case 'ahead':
            return { step: step('runner', 'Runner', 'done', compatibility.message), blocks: false };
        case 'behind':
            return {
                step: step('runner', 'Runner', 'attention', `${deployed}, v${expect.runnerVersion} expected`, `${compatibility.message} Download the runner template from this screen and ask the customer to redeploy it.`),
                blocks: false,
            };
        case 'too-old':
        case 'unreadable':
            return {
                step: step('runner', 'Runner', 'attention', deployed, `${compatibility.message} ${deploy}`),
                blocks: true,
            };
    }
};
const registryStep = (facts) => {
    if (facts.runnerUnreadable === true || facts.runnerVersion === undefined) {
        // Nothing to read it from yet. Not this step's problem to report.
        return {
            step: step('runner-registry', 'Registry token', 'unknown', 'No runner to read it from'),
            blocks: true,
        };
    }
    if (facts.registryConfigured === true) {
        return {
            step: step('runner-registry', 'Registry token', 'done', 'A token is configured'),
            blocks: false,
        };
    }
    if (facts.registryConfigured === undefined) {
        // Read and not answered is not "none configured" (IM-49).
        return { step: step('runner-registry', 'Registry token', 'unknown', ''), blocks: true };
    }
    return {
        step: step('runner-registry', 'Registry token', 'attention', 'None configured', 'The runner cannot pull the installer image. Give the customer a registry token and have them redeploy the runner template with registryUser and registryKey.'),
        blocks: true,
    };
};
const grantStep = (facts, expect, now) => {
    const title = 'Deploy access';
    const renew = (date) => `Ask the customer to run the deploy-access script's grant command with ${date}. It replaces the app's secret, so that date is the only one.`;
    if (facts.grantUnreadable === true) {
        return { step: step('deploy-grant', title, 'unknown', ''), blocks: true };
    }
    if (facts.runnerVersion === undefined &&
        facts.grantMissing !== true &&
        facts.grantExpiresAt === undefined) {
        // The secret lives in the runner's vault, so with no runner there is
        // nowhere to read it from — the registry step's reasoning.
        return {
            step: step('deploy-grant', title, 'unknown', 'No runner to read it from'),
            blocks: true,
        };
    }
    if (facts.grantMissing === true) {
        return {
            step: step('deploy-grant', title, 'attention', 'No deploy secret in the runner’s vault', renew('the date access should end, in UTC as YYYY-MM-DDTHH:MM:SSZ')),
            blocks: true,
        };
    }
    const days = grantDaysLeft(facts.grantExpiresAt, now);
    if (facts.grantExpiresAt === undefined || days === undefined) {
        return {
            step: step('deploy-grant', title, 'unknown', 'The expiry could not be read'),
            blocks: true,
        };
    }
    const ends = `Ends ${facts.grantExpiresAt}, ${describeDaysLeft(days)}`;
    const expires = new Date(facts.grantExpiresAt).getTime();
    if (expires <= now.getTime()) {
        return {
            step: step('deploy-grant', title, 'attention', ends, `Deploy access has ended and every run will be refused. ${renew('a new date')}`),
            blocks: true,
        };
    }
    /*
     * A run's token renews from the secret about every hour, so a run started
     * now is cut off at the first renewal after the expiry — the AWS v15 case
     * (IM-191), which on Azure is every run. So the whole run ceiling has to
     * fit before it.
     */
    if (azureGrantOutlastsRun(facts.grantExpiresAt, now) === false) {
        return {
            step: step('deploy-grant', title, 'attention', ends, `A run may take up to ${String(RUN_CEILING_HOURS)} hours and would be cut off partway through, so new runs are refused until access is extended. ${renew('a later date')}`),
            blocks: true,
        };
    }
    if (facts.deployAccessVersion !== undefined &&
        facts.deployAccessVersion !== expect.deployAccessVersion) {
        return {
            step: step('deploy-grant', title, 'attention', `${ends}; minted by script v${facts.deployAccessVersion}, v${expect.deployAccessVersion} expected`, `Download the deploy-access script from this screen; the next grant should be minted with v${expect.deployAccessVersion}.`),
            blocks: false,
        };
    }
    if (days <= GRANT_WARNING_DAYS) {
        return {
            step: step('deploy-grant', title, 'attention', ends, `Deploy access is approaching its end. A run in flight finishes, but a new one will be refused after it. ${renew('a later date')}`),
            blocks: false,
        };
    }
    return { step: step('deploy-grant', title, 'done', ends), blocks: false };
};
/** Whether an Azure subscription can run, and what to do next. Pure, like `assessOnboarding`. */
export const assessAzureOnboarding = (facts, expect, now = new Date()) => decide([
    delegationStep(facts, expect),
    deployAppStep(facts),
    runnerStep(facts, expect),
    registryStep(facts),
    grantStep(facts, expect, now),
]);
/**
 * An Azure subscription in the shape the onboarding screen and the preflight
 * already read (IM-49), so neither needs an Azure view of its own.
 *
 * Every step is the subscription's, because nothing in the runner is regional
 * (IM-45): one runner deploys an instance into any location. So the regions
 * carry no steps of their own, and each can run exactly when the subscription
 * can. What a region *is* on Azure is `listRegions`'s answer: a location that
 * holds an instance's resource group, which the instance template creates
 * before a first run and the installer requires to exist.
 *
 * **An empty list is a real answer** here too: delegated, runner deployed,
 * no instance created yet. That is where every Azure engagement starts.
 *
 * Two region fields are answered for the run form rather than read:
 *
 * - `suppliesRegistryKey` is the runner's registry token: the job hands it to
 *   the container as `QIM_REGISTRY_KEY` (IM-45), the IM-118 shape. It holds
 *   only once IM-46's wrapper passes it to the installer as `registry_key`.
 * - `installerChoice` is `unknown`: whether a run may choose its tag is the
 *   dispatcher's to decide, and the v1 runner runs nothing at all (IM-46).
 */
export const assessAzureAccount = (facts, regions, expect, now = new Date()) => {
    const whole = assessAzureOnboarding(facts, expect, now);
    const listed = [...new Set(regions)].sort();
    return {
        accountSteps: whole.steps,
        accountBlockedBy: whole.blockedBy,
        regions: listed.map((region) => ({
            region,
            steps: [],
            canRun: whole.canRun,
            suppliesRegistryKey: facts.registryConfigured === true,
            installerChoice: { kind: 'unknown' },
            blockedBy: [],
            ...(whole.nextAction === undefined ? {} : { nextAction: whole.nextAction }),
        })),
        canRunSomewhere: whole.canRun && listed.length > 0,
    };
};
/**
 * One instance's own step: its group exists, at a version this build expects.
 *
 * Before an instance's first run, rather than in the subscription's list,
 * because there is one per instance and the first one is made only when an
 * instance is.
 */
export const assessAzureInstance = (facts, expect) => {
    const title = 'Instance resource group';
    const deploy = 'Download the instance template from this screen and ask the customer to deploy it at subscription scope before the first run. It creates the group, its state storage and the deploy app’s roles.';
    const instance = (state, detail, nextAction) => ({
        id: 'instance-group',
        title,
        scope: 'instance',
        state,
        detail,
        ...(nextAction === undefined ? {} : { nextAction }),
    });
    if (facts.unreadable === true) {
        return decide([{ step: instance('unknown', ''), blocks: true }]);
    }
    if (facts.instanceVersion === undefined) {
        return decide([{ step: instance('attention', 'Not created', deploy), blocks: true }]);
    }
    const compatibility = checkAzureArtifact('instance', facts.instanceVersion);
    const deployed = `v${facts.instanceVersion} deployed`;
    if (compatibility.state === 'too-old' || compatibility.state === 'unreadable') {
        return decide([
            { step: instance('attention', deployed, `${compatibility.message} ${deploy}`), blocks: true },
        ]);
    }
    if (compatibility.state === 'ahead' || facts.instanceVersion === expect.instanceVersion) {
        return decide([{ step: instance('done', deployed), blocks: false }]);
    }
    return decide([
        {
            step: instance('attention', `${deployed}, v${expect.instanceVersion} expected`, `Download the instance template from this screen and ask the customer to redeploy it for this instance. v${expect.instanceVersion} is needed.`),
            blocks: false,
        },
    ]);
};
//# sourceMappingURL=azure.js.map